import React, { useState } from 'react';
import { Button, Input, Modal, Select, TextArea } from '@fasstech/middle-design-system';
import PropTypes from 'prop-types';
import { values } from 'ramda';

const JIRA_BOARDS = {
  GARANCE: {
    value: 'GARANCE',
    label: 'Garance',
    url: 'https://fasstio.atlassian.net/jira/software/c/projects/GARANCE/boards/292'
  },
  PAVPREV: {
    value: 'PAVPREV',
    label: 'Pavillon',
    url: 'https://fasstio.atlassian.net/jira/software/c/projects/PAVPREV/boards/183'
  },
  SAMIDDLE: {
    value: 'SAMIDDLE',
    label: 'SA-Middle',
    url: 'https://fasstio.atlassian.net/jira/software/c/projects/SAMIDDLE/boards/514'
  },
  MIDDLE: {
    value: 'MIDDLE',
    label: 'Middle',
    url: 'https://fasstio.atlassian.net/jira/software/c/projects/MIDDLE/boards/226'
  }
};

const JIRA_BOARD_OPTIONS = values(JIRA_BOARDS);

const PRIORITY_OPTIONS = [
  {
    value: 'Highest',
    label: 'Highest (Bloquant)'
  },
  {
    value: 'High',
    label: 'High (Haute)'
  },
  {
    value: 'Medium',
    label: 'Medium (Moyenne)'
  },
  {
    value: 'Low',
    label: 'Low (Basse)'
  },
  {
    value: 'Lowest',
    label: 'Lowest (Très basse)'
  }
];

const ENVIRONMENT_OPTIONS = [
  {
    value: 'Développement',
    label: 'Développement'
  }
];

const CreateBugModal = ({
  isOpen,
  onClose,
  bugInfo,
  onConfirm,
  isCreating,
  clientName
}) => {
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('SAMIDDLE');
  const [priority, setPriority] = useState('Medium');
  const [environment, setEnvironment] = useState('Développement');

  const handleConfirm = () => {
    onConfirm({
      title: `[${clientName}] ${title}`,
      description,
      board: JIRA_BOARDS[selectedBoard],
      priority,
      environment
    });

    setTitle('');
    setDescription('');
    setSelectedBoard('SAMIDDLE');
    setPriority('Medium');
    setEnvironment('Développement');
  };

  if (!bugInfo) return null;

  return (
    <Modal isDisplayed={isOpen} onClose={onClose} className="max-w-3xl">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">
          Créer un ticket bug Jira
        </h2>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <Select
              id="jira-board"
              label="Board Jira"
              value={selectedBoard}
              onChange={(e) => setSelectedBoard(e.target.value)}
              options={JIRA_BOARD_OPTIONS}
              required
            />
          </div>
          <div>
            <Select
              id="priority"
              label="Priorité"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              options={PRIORITY_OPTIONS}
              required
            />
          </div>
          <div>
            <Select
              id="environment"
              label="Environnement"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              options={ENVIRONMENT_OPTIONS}
              disabled
              required
            />
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center px-3 h-10 bg-gray-100 border border-gray-300 rounded-md text-gray-700 font-medium whitespace-nowrap">
              [{clientName}]
            </span>
            <Input
              id="bug-title"
              label={'Titre du ticket'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="flex-1"
              displayLabel
            />
          </div>
        </div>
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">Informations du projet</h3>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-medium">Numéro de projet:</span> {bugInfo.projectNumber}
            </div>
            <div>
              <span className="font-medium">Risque:</span> {bugInfo.risque}
            </div>
            <div>
              <span className="font-medium">Étape:</span> {bugInfo.viewLabel}
            </div>
            <div>
              <span className="font-medium">View ID:</span> {bugInfo.viewId}
            </div>
            <div>
              <span className="font-medium">URL:</span>{' '}
              <span className="text-primary break-all">{bugInfo.url}</span>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-3 italic">
            ℹ️ La trame complète du projet sera ajoutée en pièce jointe JSON au ticket.
          </p>
        </div>

        <div className="mb-6">

          <TextArea
            id="bug-description"
            label={'Description du bug'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-40 resize-vertical"
            required
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isCreating}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!title.trim() || !description.trim() || isCreating}
            loading={isCreating}>
            {isCreating ? 'Création en cours...' : 'Créer le ticket'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

CreateBugModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bugInfo: PropTypes.shape({
    projectNumber: PropTypes.string,
    risque: PropTypes.string,
    viewLabel: PropTypes.string,
    viewId: PropTypes.string,
    url: PropTypes.string,
    environment: PropTypes.string
  }),
  onConfirm: PropTypes.func.isRequired,
  isCreating: PropTypes.bool,
  clientName: PropTypes.string
};

export default CreateBugModal;
